import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { components } from "@/lib/openapi";
import { useCheckpoints, useUpdateSecurityLane } from "@/queries/security";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Pencil } from "lucide-react";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

type SecurityLaneSchema = components["schemas"]["SecurityLaneSchema"];
type SecurityLaneUpdateSchema =
  components["schemas"]["SecurityLaneUpdateSchema"];

const updateLaneFormSchema = z
  .object({
    name: z.string().min(1, "Lane name is required"),
    type: z.enum(["STANDARD", "PRECHECK"]),
    start_interval: z.number().min(0).max(144, "Invalid time interval"),
    end_interval_display: z.number().min(0).max(144, "Invalid time interval"),
    crosses_midnight: z.boolean().default(false),
  })
  .refine(
    (data) => {
      if (!data.crosses_midnight) {
        return data.end_interval_display > data.start_interval;
      }
      return true;
    },
    {
      message:
        "End time must be after start time, or enable 'Crosses midnight'",
      path: ["end_interval_display"],
    }
  );

type UpdateLaneFormValues = z.infer<typeof updateLaneFormSchema>;

interface UpdateSecurityLaneDialogProps {
  lane: SecurityLaneSchema;
}

export function UpdateSecurityLaneDialog({
  lane,
}: UpdateSecurityLaneDialogProps) {
  const [open, setOpen] = useState(false);
  const { data: checkpoints } = useCheckpoints();
  const updateMutation = useUpdateSecurityLane();

  const crossesMidnight = lane.end_interval > 144;
  const endIntervalDisplay =
    lane.end_interval % 144 === 0 && lane.end_interval > 0
      ? 144
      : lane.end_interval % 144;

  const form = useForm<UpdateLaneFormValues>({
    resolver: zodResolver(updateLaneFormSchema),
    defaultValues: {
      name: lane.name,
      type: lane.type as "STANDARD" | "PRECHECK",
      start_interval: lane.start_interval,
      end_interval_display: endIntervalDisplay,
      crosses_midnight: crossesMidnight,
    },
  });

  const generateTimeOptions = () => {
    const options = [];
    for (let i = 0; i <= 144; i++) {
      const hours = Math.floor((i * 10) / 60);
      const minutes = (i * 10) % 60;
      const time = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      options.push({ value: i, label: time });
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const checkpointName = useMemo(() => {
    if (!checkpoints) return `ID: ${lane.checkpoint_id}`;
    const checkpoint = checkpoints.find((c) => c.id === lane.checkpoint_id);
    return checkpoint?.name || `ID: ${lane.checkpoint_id}`;
  }, [checkpoints, lane.checkpoint_id]);

  const handleSubmit = (values: UpdateLaneFormValues) => {
    let end_interval = values.end_interval_display;
    if (values.crosses_midnight) {
      end_interval = values.end_interval_display + 144;
    }

    const payload: SecurityLaneUpdateSchema = {
      name: values.name,
      type: values.type,
      start_interval: values.start_interval,
      end_interval: end_interval,
    };

    updateMutation.mutate(
      { id: lane.id, data: payload },
      {
        onSuccess: () => {
          setOpen(false);
        },
      }
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      form.reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Security Lane</DialogTitle>
          <DialogDescription>
            Update the security lane configuration.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Lane 1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>Checkpoint</FormLabel>
              <FormControl>
                <Input value={checkpointName} disabled />
              </FormControl>
            </FormItem>

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="STANDARD">Standard</SelectItem>
                      <SelectItem value="PRECHECK">PreCheck</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="start_interval"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Operating Hours - Start Time</FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select start time" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {timeOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value.toString()}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="end_interval_display"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Operating Hours - End Time</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      const newValue = parseInt(value);
                      field.onChange(newValue);
                      const startInterval = form.getValues("start_interval");
                      if (
                        newValue < startInterval &&
                        !form.getValues("crosses_midnight")
                      ) {
                        form.setValue("crosses_midnight", true);
                      }
                    }}
                    value={field.value?.toString()}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select end time" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {timeOptions.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value.toString()}
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="crosses_midnight"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      Crosses midnight (operates into next day)
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <div className="rounded-md border bg-muted/50 p-3 text-sm space-y-1">
              <div className="font-medium text-foreground">Preview:</div>
              <div>
                {(() => {
                  const start = form.watch("start_interval");
                  const endDisplay = form.watch("end_interval_display");
                  const crosses = form.watch("crosses_midnight");

                  const startHour = Math.floor((start * 10) / 60);
                  const startMin = (start * 10) % 60;

                  const endHour =
                    endDisplay === 144
                      ? 24
                      : Math.floor((endDisplay * 10) / 60);
                  const endMin =
                    endDisplay === 144 ? 0 : (endDisplay * 10) % 60;

                  const startTime = `${startHour.toString().padStart(2, "0")}:${startMin.toString().padStart(2, "0")}`;
                  const endTime = `${endHour.toString().padStart(2, "0")}:${endMin.toString().padStart(2, "0")}`;

                  const duration = crosses
                    ? (144 - start + endDisplay) / 6
                    : (endDisplay - start) / 6;

                  return (
                    <>
                      <div className="text-foreground">
                        {startTime} - {endTime}
                        {crosses && (
                          <span className="text-primary font-medium">
                            {" "}
                            (next day)
                          </span>
                        )}
                      </div>
                      <div className="text-muted-foreground">
                        Duration: {duration.toFixed(1)} hours
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {updateMutation.isError && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                <span>Failed to update security lane. Please try again.</span>
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update Lane"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
